import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import {
  autoSignIn,
  getCurrentUser,
  signIn,
  signUp,
  signOut,
} from "aws-amplify/auth";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { JSX, useState } from "react";
import { login } from "@realtorSitesClient/store/authSlice";
import { useModal } from "@realtorSitesClient/hooks/useModal";
import { useToast } from "@uicomponents/hooks/use-toast";
import OtpForm from "./OtpForm";
import { useAppDispatch } from "@realtorSitesClient/hooks/redux";
import * as Sentry from "@sentry/react";

enum RegisterStage {
  ENTER_DETAILS,
  CONFIRM_EMAIL,
}

const registrationSchema = z
  .object({
    firstName: z.string().min(1, "First name is required"),
    lastName: z.string().min(1, "Last name is required"),
    email: z.string().email("Invalid email address"),
    phone: z
      .string()
      .min(10, "Phone number must be at least 10 digits")
      .regex(/^[+\d\s\-().]+$/, "Invalid phone number format"),
    password: z
      .string()
      .min(8, "Password must be at least 8 characters long")
      .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
      .regex(/[a-z]/, "Password must contain at least one lowercase letter")
      .regex(/[0-9]/, "Password must contain at least one number")
      .regex(
        /[!@#$%^&*(),.?":{}|<>]/,
        "Password must contain at least one special character",
      ),
    confirmPassword: z.string().min(1, "Please confirm your password"),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

const RegisterTab = ({ onLogin }: { onLogin?: () => void }) => {
  const dispatch = useAppDispatch();
  const { toast } = useToast();
  const { closeModal } = useModal();
  const [registerStage, setRegisterStage] = useState<RegisterStage>(
    RegisterStage.ENTER_DETAILS,
  );
  const [otpDestination, setOtpDestination] = useState<string>("");
  const {
    register: registerRegister,
    handleSubmit: handleSubmitRegister,
    watch: watchRegister,
    setError: setErrorRegister,
    formState: {
      errors: errorsRegister,
      isValid: isValidRegister,
      isSubmitting: isSubmittingRegister,
      isSubmitSuccessful: isSubmitSuccessfulRegister,
    },
  } = useForm<z.infer<typeof registrationSchema>>({
    resolver: zodResolver(registrationSchema),
    mode: "onTouched",
  });

  async function onRegisterSubmit(data: z.infer<typeof registrationSchema>) {
    try {
      await signOut();
    } catch (error) {
      Sentry.captureException(new Error("error signing out: " + error));
    }

    await signUp({
      username: data.email,
      password: data.password,
      options: {
        userAttributes: {
          given_name: data.firstName,
          family_name: data.lastName,
          email: data.email,
          phone_number: data.phone.startsWith("+")
            ? data.phone
            : `+1${data.phone.replace(/\D/g, "")}`,
        },
        autoSignIn: {
          authFlowType: "USER_SRP_AUTH",
        },
      },
    })
      .then(({ nextStep }) => {
        if (nextStep.signUpStep === "CONFIRM_SIGN_UP") {
          setOtpDestination(data.email);
          setRegisterStage(RegisterStage.CONFIRM_EMAIL);
        } else if (nextStep.signUpStep === "DONE") {
          return getCurrentUser().then((cognitoUser) => {
            dispatch(login(cognitoUser));
            if (onLogin) onLogin();
            closeModal();
          });
        } else if (nextStep.signUpStep === "COMPLETE_AUTO_SIGN_IN") {
          return autoSignIn().then(async ({ nextStep }) => {
            if (nextStep.signInStep === "DONE") {
              return getCurrentUser().then((cognitoUser) => {
                dispatch(login(cognitoUser));
                if (onLogin) onLogin();
                closeModal();
              });
            }
          });
        } else {
          console.warn(`Unsupported sign-up step: ${nextStep}`);
        }
      })
      .catch(async (error) => {
        console.error("Error during sign up:", error);
        let message = error.message || "Registration failed";

        if (message.includes("PreSignUp failed with error")) {
          message = message.replace("PreSignUp failed with error ", "");
        }

        toast({
          title: "Registration Failed",
          description:
            error instanceof Error
              ? message
              : "An unknown error occurred during registration.",
        });
        setErrorRegister("root", {
          type: "manual",
          message,
        });
      });
  }

  let RegisterContent: JSX.Element | null = null;

  switch (registerStage) {
    case RegisterStage.ENTER_DETAILS:
      RegisterContent = (
        <form
          onSubmit={handleSubmitRegister(onRegisterSubmit, (err) => {
            Sentry.captureException(err);
          })}
        >
          <Box display={"flex"} flexDirection={"column"} gap={"10px"}>
            <Box display={"flex"} gap={"10px"}>
              <TextField
                fullWidth
                label="First Name"
                variant="standard"
                size="small"
                slotProps={{
                  input: { ...registerRegister("firstName") },
                }}
                error={!!errorsRegister.firstName}
                helperText={
                  errorsRegister.firstName?.message?.toString() || " "
                }
              />
              <TextField
                fullWidth
                label="Last Name"
                variant="standard"
                size="small"
                slotProps={{
                  input: { ...registerRegister("lastName") },
                }}
                error={!!errorsRegister.lastName}
                helperText={errorsRegister.lastName?.message?.toString() || " "}
              />
            </Box>
            <TextField
              label="Email"
              size="small"
              fullWidth
              variant="standard"
              autoComplete="email"
              slotProps={{
                input: { ...registerRegister("email") },
              }}
              error={!!errorsRegister.email}
              helperText={errorsRegister.email?.message?.toString() || " "}
            />
            <TextField
              label="Phone Number"
              size="small"
              fullWidth
              variant="standard"
              autoComplete="tel"
              slotProps={{
                input: { ...registerRegister("phone") },
              }}
              error={!!errorsRegister.phone}
              helperText={errorsRegister.phone?.message?.toString() || " "}
            />
            <TextField
              label="Password"
              placeholder="Enter your password"
              type="password"
              size="small"
              variant="standard"
              autoComplete="new-password"
              {...registerRegister("password")}
              error={!!errorsRegister.password}
              helperText={errorsRegister.password?.message?.toString() || " "}
            />
            <TextField
              label="Confirm Password"
              placeholder="Confirm your password"
              type="password"
              size="small"
              variant="standard"
              autoComplete="new-password"
              {...registerRegister("confirmPassword")}
              error={!!errorsRegister.confirmPassword}
              helperText={
                errorsRegister.confirmPassword?.message?.toString() || " "
              }
            />
            <Button
              disabled={
                !isValidRegister ||
                isSubmittingRegister ||
                isSubmitSuccessfulRegister
              }
              type="submit"
              variant="outlined"
              sx={{ mt: 3 }}
            >
              Register
            </Button>
          </Box>
        </form>
      );
      break;
    case RegisterStage.CONFIRM_EMAIL:
      RegisterContent = (
        <OtpForm
          username={watchRegister("email")}
          destination={otpDestination}
          onSuccess={async () => {
            return signIn({
              username: watchRegister("email"),
              password: watchRegister("password"),
            }).then(async ({ nextStep }) => {
              if (nextStep.signInStep === "DONE") {
                return getCurrentUser().then((cognitoUser) => {
                  dispatch(login(cognitoUser));
                  closeModal();
                });
              }
            });
          }}
        />
      );
      break;
    default:
  }

  return (
    <Box display={"flex"} sx={{ width: "100%" }}>
      <Box sx={{ mx: "auto", width: "100%" }}>{RegisterContent}</Box>
    </Box>
  );
};

export default RegisterTab;
